<template>
  <form class="donor-form-card" @submit.prevent="submitLead">
    <div class="donor-form-header">
      <h2 class="donor-form-title">Contact Form</h2>
    </div>

    <section class="donor-form-section">
      <div class="donor-form-address">
        <div class="donor-form-grid-thirds">
          <div class="donor-form-field-two-thirds">
            <InputField
              label-size="small"
              input-text-size="small"
              v-model:value="form.addressStreet.value"
              :errors="form.addressStreet.errors"
              label="Street address"
              type="text"
              text="street-address"
              id="street-address"
              placeholder=""
            />
          </div>
          <InputField
            label-size="small"
            input-text-size="small"
            v-model:value="form.addressDetail.value"
            :errors="form.addressDetail.errors"
            label="Apt, Floor, or Suite"
            type="text"
            text="lead-address-detail"
            id="lead-address-detail"
            placeholder="Optional"
          />
        </div>
        <div class="donor-form-grid-thirds">
          <InputField
            label-size="small"
            input-text-size="small"
            v-model:value="form.city.value"
            :errors="form.city.errors"
            label="City"
            type="text"
            text="lead-city"
            id="lead-city"
            placeholder="City"
            autocomplete="address-level2"
          />
          <SelectField
            small
            :select-options="usStateData"
            v-model:value="form.state.value"
            label="State"
            :errors="form.state.errors"
          />
          <InputField
            label-size="small"
            input-text-size="small"
            v-model:value="form.postalCode.value"
            :errors="form.postalCode.errors"
            label="Zip / Postal code"
            type="text"
            text="lead-postal-code"
            id="lead-postal-code"
            placeholder="ZIP"
            autocomplete="postal-code"
          />
        </div>
      </div>
    </section>

    <section class="donor-form-section donor-form-section-divider">
      <p class="donor-form-section-title">Contact information</p>
      <div class="donor-form-grid">
        <InputField
          label-size="small"
          input-text-size="small"
          v-model:value="form.firstName.value"
          :errors="form.firstName.errors"
          @blur="form.firstName.validate()"
          label="First name"
          type="text"
          text="lead-first-name"
          id="lead-first-name"
          placeholder="First name"
        />
        <InputField
          label-size="small"
          input-text-size="small"
          v-model:value="form.lastName.value"
          :errors="form.lastName.errors"
          @blur="form.lastName.validate()"
          label="Last name"
          type="text"
          text="lead-last-name"
          id="lead-last-name"
          placeholder="Last name"
        />
        <div class="donor-form-field-full">
          <InputField
            label-size="small"
            input-text-size="small"
            v-model:value="form.email.value"
            :errors="form.email.errors"
            @blur="form.email.validate()"
            label="Email"
            type="email"
            text="lead-email"
            id="lead-email"
            placeholder="example@email.com"
            autocomplete="email"
          />
        </div>
        <InputField
          label-size="small"
          input-text-size="small"
          v-model:value="form.mobilePhone.value"
          :errors="form.mobilePhone.errors"
          @blur="form.mobilePhone.validate()"
          label="Phone"
          type="text"
          text="lead-phone"
          id="lead-phone"
          placeholder="XXX-XXX-XXXX"
          autocomplete="phone"
          pattern="[0-9]{3}-[0-9]{3}-[0-9]{4}"
          title="Phone number should be in the format XXX-XXX-XXXX"
        />
      </div>
    </section>

    <section class="donor-form-section donor-form-section-divider">
      <p class="donor-form-section-title">Date of birth</p>
      <div class="donor-form-grid-thirds">
        <SelectField
          small
          :select-options="monthData"
          v-model:value="form.birthMonth.value"
          :errors="form.birthMonth.errors"
          label="Month"
        />
        <InputField
          label-size="small"
          input-text-size="small"
          v-model:value="form.birthDay.value"
          :errors="form.birthDay.errors"
          @blur="form.birthDay.validate()"
          label="Day"
          type="number"
          text="lead-birth-day"
          id="lead-birth-day"
          placeholder="DD"
        />
        <SelectField
          small
          :select-options="years"
          v-model:value="form.birthYear.value"
          :errors="form.birthYear.errors"
          label="Year"
        />
      </div>
    </section>

    <div class="donor-form-actions">
      <BaseButton type="submit" id="lead-info-submit">Submit</BaseButton>
    </div>

    <Modal
      headerText="You need to be at least 18 years old to sign up."
      ctaText="Sorry about that!"
      :modalOpen="this.tooYoungModalOpen"
      @closeModal="toggleTooYoungModal"
      :showFailureIcon="true"
    />
  </form>
</template>

<script>
import { ref, computed } from 'vue'
import SelectField from '@/components/inputs/SelectField'
import InputField from '@/components/inputs/InputField'
import { LeadForm } from '@/services/leads/'
import usStateData from '@/data/usStateData.js'
import monthData from '@/data/monthData.js'
import Modal from '@/components/Modal'

export default {
  name: 'LeadFormInfo',
  components: {
    SelectField,
    InputField,
    Modal,
  },
  emits: ['leadInfo', 'submitForm'],
  setup(_, { emit }) {
    const currentYear = new Date().getFullYear()
    const years = computed(() => {
      return Array.from({ length: currentYear - 1900 }, (value, index) => {
        const year = 1901 + index
        return { value: year, text: `${year}` }
      }).reverse()
    })
    const birthYear = ref(currentYear)
    const tooYoungModalOpen = ref(false)
    const form = ref(new LeadForm())

    function submitLead() {
      const unwrappedForm = form.value
      unwrappedForm.validate()

      if (!unwrappedForm.isValid) return

      if (handleAgeCheck() === false) {
        toggleTooYoungModal()
        return
      }
      emit('submitForm', form.value.formVal)
    }

    function toggleTooYoungModal() {
      tooYoungModalOpen.value = !tooYoungModalOpen.value
    }

    function handleAgeCheck() {
      const unwrappedForm = form.value
      const today = new Date()
      const birthDate = new Date(
        unwrappedForm.birthYear.value,
        unwrappedForm.birthMonth.value,
        unwrappedForm.birthDay.value,
      )

      let age = today.getFullYear() - birthDate.getFullYear()
      const m = today.getMonth() + 1 - birthDate.getMonth()

      if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
        age--
      }

      if (age < 18) {
        return false
      }
    }

    return {
      form,
      birthYear,
      monthData,
      usStateData,
      years,
      tooYoungModalOpen,
      submitLead,
      toggleTooYoungModal,
    }
  },
}
</script>
